import productionConfig from 'CONFIG/env/production';
import { checkNested } from 'MODULES/checkNested';
import getCurrentHostname from 'MODULES/getCurrentHostname';

let _loadPromise = null;
let _sentry = null;

const SENTRY_DSN = checkNested(productionConfig, 'SENTRY_KEY');
const isProdHost = () => getCurrentHostname().indexOf('pp.lv') !== -1;
const isHeadless = () => /HeadlessChrome/.test(window.navigator.userAgent);

// Web Vitals RUM: this share of page loads starts Sentry when the browser is idle after load,
// with browser tracing (LCP, CLS, INP + the element behind each). Other loads keep Sentry error-only and lazy.
// Off (0): the DSN key rate limit (10 events/min) is used up by errors, so the SDK backs off and almost no
// Web Vitals data arrived, while sampled loads still paid for Sentry + tracing (CLASF-6721).
const WEB_VITALS_SAMPLE_RATE = 0;
const isWebVitalsSampled = Math.random() < WEB_VITALS_SAMPLE_RATE;

const LANGS = ['lv', 'ru'];
const NON_CATEGORY_SECTIONS = [
    'about', 'checkout', 'company', 'compare', 'contacts', 'cooperative', 'digest', 'help', 'leasing', 'loan',
    'qr', 'referral', 'renew', 'service', 'subscription-plans', 'user', 'user-data', 'xml-docs'
];

// Groups URLs into a few page types, so Sentry shows p75 per type instead of per URL.
export const getPageType = (pathname = '') => {
    const parts = pathname.split('/').filter(Boolean);
    if (!parts.length || (parts.length === 1 && LANGS.includes(parts[0]))) return 'home';
    if (/\/![0-9]+/.test(pathname)) return 'lot';
    if (!LANGS.includes(parts[0])) return 'other';
    if (parts[1] === 'search') return 'search';
    if (parts[1] === 'landing') return 'landing';
    if (parts[1] === 'modal-map') return 'map';
    if (NON_CATEGORY_SECTIONS.includes(parts[1])) return 'other';

    return 'category';
};

const GTM_CONSENT_ERROR_MESSAGES = [
    'Cannot read properties of undefined (reading \'consents\')',
    'Cannot read property \'consents\' of undefined'
];

function isGTMConsentError(event) {
    const exceptionValues = checkNested(event, ['exception', 'values']) || [];
    const hasConsentMessage = GTM_CONSENT_ERROR_MESSAGES.some(message => {
        if (event.message && event.message.indexOf(message) !== -1) return true;

        return exceptionValues.some(value => value && value.value && value.value.indexOf(message) !== -1);
    });

    if (!hasConsentMessage) return false;

    return exceptionValues.some(value => {
        const frames = checkNested(value, ['stacktrace', 'frames']) || [];

        return frames.some(frame => frame && frame.function === 'updateGTMConsent');
    });
}

// InMobi CMP (loaded by GTM) throws inside its own bundle, mostly on Firefox; nothing to fix on our side.
const VENDOR_ONLY_URL = /^https:\/\/cmp\.inmobi\.com\//;

function isVendorOnlyError(event) {
    const exceptionValues = checkNested(event, ['exception', 'values']) || [];
    const frames = exceptionValues.reduce((all, value) => all.concat(checkNested(value, ['stacktrace', 'frames']) || []), []);

    return frames.length > 0 && frames.every(frame => frame && VENDOR_ONLY_URL.test(frame.filename || ''));
}

// Lazy import the SDK only once
export function loadSentry() {
    if (!isProdHost() || isHeadless()) return Promise.resolve(null);
    if (_loadPromise) return _loadPromise;
    _loadPromise = import(/* webpackChunkName: "sentry" */ '@sentry/react')
        .then(mod => {
            _sentry = mod;
            if (!SENTRY_DSN) return _sentry;

            // Minimal init — no tracing, no replays, no feedback UI
            _sentry.init({
                dsn: SENTRY_DSN,
                environment: 'production',
                // Performance only for page loads sampled for Web Vitals; navigations are not traced.
                tracesSampleRate: isWebVitalsSampled ? 1 : 0,
                replaysSessionSampleRate: 0, // disables session replays (if present)
                replaysOnErrorSampleRate: 0, // disables replays-on-error
                autoSessionTracking: false, // keep lean
                integrations: isWebVitalsSampled ? [
                    _sentry.browserTracingIntegration({
                        instrumentNavigation: false,
                        // CLS for the whole page view, sent on page hide (as CrUX counts it), not only until the pageload span ends.
                        _experiments: { enableStandaloneClsSpans: true },
                        beforeStartSpan: options => ({ ...options, name: getPageType(window.location.pathname) })
                    })
                ] : [],
                // Optionally tag release if you inject it into window.__APP_RELEASE__
                // release: window.__APP_RELEASE__,
                beforeSend(event) {
                    if (isGTMConsentError(event)) return null;
                    if (isVendorOnlyError(event)) return null;

                    return event;
                }
            });

            if (isWebVitalsSampled) {
                const lang = window.location.pathname.split('/')[1];
                // Standalone CLS / INP spans take the scope transaction name (not tags), so they group by page type too.
                _sentry.getCurrentScope().setTransactionName(getPageType(window.location.pathname));
                _sentry.setTags({
                    page_type: getPageType(window.location.pathname),
                    lang: LANGS.includes(lang) ? lang : (document.documentElement.lang || 'lv'),
                    // UA check like the home preview bootstrap; react-device-detect would add weight to the boot bundle.
                    device: /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(window.navigator.userAgent) ? 'mobile' : 'desktop'
                });
            }

            return _sentry;
        })
        .catch(() => null);

    return _loadPromise;
}

export async function reportError(error, context) {
    try {
        if (!isProdHost() || isHeadless()) {
            return;
        }
        const sentry = await loadSentry();
        if (sentry && sentry.captureException) {
            sentry.captureException(error, context);
        }
        // eslint-disable-next-line no-empty
    } catch (e) {}
}

// Optional message capture
export async function reportMessage(message, context) {
    try {
        if (!isProdHost() || isHeadless()) return;
        const sentry = await loadSentry();
        if (sentry && sentry.captureMessage) {
            sentry.captureMessage(message, context);
        }
        // eslint-disable-next-line no-empty
    } catch (e) {}
}

// Expose a manual initializer if you ever want to pre-load on-demand
export function warmupSentry() {
    return loadSentry();
}

// Starts Sentry for page loads sampled for Web Vitals, after load and when the browser is idle,
// so it does not compete with the page itself. Buffered LCP / CLS entries are still read.
export function startWebVitalsSampling() {
    if (!isWebVitalsSampled || !isProdHost() || isHeadless()) return;

    const start = () => {
        const idle = window.requestIdleCallback || (cb => setTimeout(cb, 1));
        idle(() => loadSentry(), { timeout: 5000 });
    };

    if (document.readyState === 'complete') {
        start();
    } else {
        window.addEventListener('load', start, { once: true });
    }
}
